import { getLatestValue } from '.'

import React, { useMemo } from 'react'

import _find from 'lodash/find'

import { CreditReport } from '@/graphql/generated'
import { useListApplicationProfilesQuery } from '@/services/v2-application/applicationProfile'
import { ApplicationProfile, Profile } from '@/types/unlock'

export function useCreditReports(applicationUuid, creditReports: CreditReport[]) {
  const { data: profiles } = useListApplicationProfilesQuery(applicationUuid, {
    skip: !applicationUuid,
  })

  const applicationProfiles = profiles?.results || []

  const applicants: Array<ApplicationProfile | undefined> = useMemo(() => {
    return [1, 2, 3, 4, 5].map((position) => _find(applicationProfiles, { position: position }))
  }, [applicationProfiles])

  const getShortNameByProfile = (profile: Profile) => {
    return profile ? `${profile?.first_name} ${profile?.last_name[0]}.` : ''
  }

  const shortNames = useMemo(() => {
    return applicants.map((applicant) =>
      applicant ? getShortNameByProfile(applicant.profile) : undefined
    )
  }, [applicants])
  const realCreditReports = useMemo(
    () => (creditReports?.length > 0 ? creditReports.filter((c) => !!c.reportOrderedAt) : []),
    [creditReports]
  )

  const realCreditReport = useMemo(
    () => (creditReports?.length > 0 ? getLatestValue(realCreditReports) : null),
    [creditReports]
  )

  const getApplicantNamesByReport = (report: CreditReport) => {
    const reportApplicants = applicants.filter((applicant) =>
      report?.applicants?.find(
        (reportApplicant) => +reportApplicant?.applicationProfile?.id === applicant?.id
      )
    )

    return reportApplicants?.map((applicant) => (
      <p key={applicant?.id}>
        {applicant?.position}. {getShortNameByProfile(applicant?.profile)}
      </p>
    ))
  }

  function getProfilePosition(applicantId) {
    const profile = applicationProfiles?.find((profile) => profile.id == applicantId)
    return profile ? profile.position : Infinity
  }

  function compareCreditReports(a: CreditReport, b: CreditReport) {
    const positionsA = a.applicants.map((applicant) =>
      getProfilePosition(applicant.applicationProfile.id)
    )

    const positionsB = b.applicants.map((applicant) =>
      getProfilePosition(applicant.applicationProfile.id)
    )

    for (let i = 0; i < positionsA.length; i++) {
      if (positionsA[i] !== positionsB[i]) {
        return positionsA[i] - positionsB[i]
      }
    }

    return 0
  }

  const sortedCreditReports = useMemo(() => {
    return creditReports?.map((report) => report).sort(compareCreditReports)
  }, [creditReports, applicationProfiles])

  return {
    applicants,
    shortNames,
    getApplicantNamesByReport,
    realCreditReport,
    sortedCreditReports,
  }
}
